import type { AlpineComponent } from 'alpinejs'
import Swiper from 'swiper'
import { Pagination, Navigation, A11y, Autoplay } from 'swiper/modules'
import type { SwiperOptions } from 'swiper/types'
import 'swiper/css/bundle'
import 'swiper/css/pagination'

import gsap from 'gsap'
import { SplitText } from 'gsap/all'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)
gsap.registerPlugin(SplitText)

// if better types get needed check
// https://github.com/alpinejs/alpine/issues/2199#issuecomment-1809892127

export function Slider(
  config: SwiperOptions,
  heroType: boolean = false,
): AlpineComponent<{
  swiper: Swiper | undefined
  init(): void
  discard(): void
  animateHeroSlide(
    slide: HTMLElement | undefined,
    firstSlide: boolean,
    direction: 'next' | 'prev',
  ): void
}> {
  return {
    swiper: undefined,

    init() {
      let mm = gsap.matchMedia()

      const defaultConfig: SwiperOptions = {
        modules: [Pagination, Navigation, A11y, Autoplay],
        slidesPerView: 1,
        loop: true,
        spaceBetween: 30,
        pagination: {
          el: this.$refs.pagination,
          type: 'bullets',
          clickable: true,
          bulletClass:
            'inline-block rounded-full w-1.5 h-1.5 ring-1 ring-gray-300 ring-offset-2 ring-offset-white cursor-pointer',
          bulletActiveClass: 'bg-gray-300',
        },
        navigation: {
          nextEl: this.$refs.next,
          prevEl: this.$refs.prev,
          disabledClass: 'hidden cursor-not-allowed',
        },
        a11y: {
          enabled: true,
        },
        on: {
          init: (swiper) => {
            if (heroType) {
              mm.add('(prefers-reduced-motion: no-preference)', () => {
                const firstSlide = swiper.slides[swiper.activeIndex]

                this.animateHeroSlide(firstSlide, true, swiper.swipeDirection)
              })
            }
          },
        },
      }

      if (!this.$refs.slider) return

      this.swiper = new Swiper(this.$refs.slider, {
        ...defaultConfig,
        ...config,
      })

      this.swiper.on('slideChange', (swiper) => {
        mm.add('(prefers-reduced-motion: no-preference)', () => {
          const currentRealIndex = swiper.realIndex ?? 0 // Get the real index (useful with loop mode)
          const currentSlideElement = swiper.slides[currentRealIndex]

          this.animateHeroSlide(
            currentSlideElement,
            false,
            swiper.previousIndex < swiper.activeIndex ? 'next' : 'prev',
          )
        })
      })
    },

    discard() {
      this.swiper && this.swiper.destroy()
    },

    animateHeroSlide(slide, firstSlide = false, direction) {
      const tl = gsap.timeline({
        defaults: {
          ease: 'power2.inOut',
        },
        scrollTrigger: {
          trigger: slide,
          start: 'top 60%',
        },
      })

      const title = slide?.getElementsByClassName('slide-title')[0]
      const image = slide?.getElementsByClassName('slide-image')[0]
      const gradient = slide?.getElementsByClassName('slide-image-gradient')[0]
      const details = slide?.querySelectorAll('.slide-details > ul')
      const cta = slide?.getElementsByClassName('slide-cta')[0]
      const pagination = slide?.getElementsByClassName('slide-pagination')[0]

      let split = null

      if (image) {
        tl.fromTo(
          image,
          {
            scale: 1.2,
            clipPath:
              direction === 'next' ? 'inset(0 0 0 100%)' : 'inset(0 100% 0 0)',
            webkitClipPath:
              direction === 'next'
                ? 'inset(0% 0% 0% 100%)'
                : 'inset(0% 100% 0% 0%)',
          },
          {
            scale: 1,
            clipPath: 'inset(0% 0% 0% 0%)',
            webkitClipPath: 'inset(0% 0% 0% 0%)',
            duration: firstSlide ? 0.8 : 0.4,
            ease: 'circ.inOut',
          },
        )

        if (gradient) {
          tl.fromTo(
            gradient,
            {
              opacity: 0,
            },
            {
              opacity: 1,
              duration: 0.5,
            },
            '-=0.5',
          )
        }
      }

      if (title) {
        split = new SplitText(title, { type: 'words' })

        split.words.forEach((word) => {
          const wrapper = document.createElement('span')
          wrapper.style.overflow = 'hidden'
          wrapper.style.display = 'inline-block'
          wrapper.style.verticalAlign = 'bottom'

          word.parentNode?.insertBefore(wrapper, word)
          wrapper.appendChild(word)
        })

        tl.from(
          split.words,
          {
            opacity: 0,
            yPercent: 120,
            duration: 0.8,
            ease: 'power4.out',
            stagger: {
              each: 0.1,
              from: 'start',
            },
          },
          firstSlide ? 1 : 0.5,
        )
      }

      if (details) {
        for (const detail of details) {
          // console.log(detail)

          tl.fromTo(
            detail,
            {
              opacity: 0,
              yPercent: 120,
            },
            {
              opacity: 1,
              yPercent: 0,
              duration: 0.8,
              ease: 'power4.out',
            },
            firstSlide ? 1 : 0.5,
          )
        }
      }

      if (cta) {
        tl.fromTo(
          cta,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.25,
            ease: 'power4.out',
          },
          firstSlide ? 1 : 0.5,
        )
      }

      if (pagination) {
        tl.fromTo(
          pagination,
          {
            opacity: 0,
          },
          {
            opacity: 1,
            duration: 0.25,
            ease: 'power4.out',
          },
          firstSlide ? 2 : 1,
        )
      }
    },
  }
}
